import { Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { Control, Controller, FieldPath, FieldValues, RegisterOptions } from 'react-hook-form'

import { cn } from '@/lib/utils'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface TextInputProps<T extends FieldValues> {
  control: Control<T>
  label: string
  name: FieldPath<T>
  placeholder?: string
  type?: string
  rules?: RegisterOptions<T>
  errors?: Record<string, any>
  className?: string
  inputClassName?: string
  disabled?: boolean
  multiline?: boolean
  rows?: number
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void

  pattern?: number[]
  separator?: string
  enableFormat?: boolean
  maxLength?: number
  min?: number | string
  max?: number | string
  icon?: React.ReactNode
  valueAsNumber?: boolean
  emptyAsUndefined?: boolean
  upperCase?: boolean
  rightElement?: React.ReactNode
}

export function TextInput<T extends FieldValues>({
  control,
  label,
  name,
  placeholder = '',
  type = 'text',
  rules,
  errors,
  className = '',
  inputClassName = '',
  rows = 3,
  disabled = false,
  multiline = false,
  onChange,
  onKeyDown,
  pattern,
  separator = '',
  enableFormat = false,
  maxLength,
  min,
  max,
  icon,
  rightElement,
  valueAsNumber,
  upperCase,
  emptyAsUndefined = true,
}: TextInputProps<T>) {
  const [showPassword, setShowPassword] = useState(false)
  const errorMessage = errors?.[name as string]?.message

  const formatValue = (raw: string) => {
    const digits = raw.replace(/\D/g, '')
    let result = ''
    let index = 0

    pattern?.forEach((len, i) => {
      if (digits.length > index) {
        result += digits.substring(index, index + len)
        index += len
        if (i < pattern.length - 1 && digits.length > index) {
          result += separator
        }
      }
    })

    return result
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && <Label htmlFor={name as string} className="text-sm text-ink">{label}</Label>}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <div className="relative">
              {icon && (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                  {icon}
                </div>
              )}
              {multiline ? (
                <Textarea
                  id={name as string}
                  placeholder={placeholder}
                  rows={rows}
                  disabled={disabled}
                  maxLength={maxLength}
                  {...field}
                  value={field.value ?? ''}
                  className={cn(
                    (error || errorMessage) && 'border-destructive',
                    icon && 'pl-10',
                    rightElement && 'pr-10',
                    inputClassName
                  )}
                />
              ) : (
                <Input
                  id={name as string}
                  type={type === 'password' && showPassword ? 'text' : type}
                  placeholder={placeholder}
                  disabled={disabled}
                  maxLength={maxLength}
                  min={min}
                  max={max}
                  {...field}
                  inputMode='numeric'
                  value={field.value ?? ''}
                  className={cn(
                    (error || errorMessage) && 'border-destructive',
                    icon && 'pl-10',
                    (type === 'password' || rightElement) && 'pr-10',
                    inputClassName
                  )}
               onChange={(e) => {
  let value = e.target.value

  if (enableFormat) {
    value = formatValue(value)
  }

  if (upperCase) {
    value = value.toUpperCase()
  }

  field.onChange(value)
  onChange?.(e)
}}
                  onKeyDown={onKeyDown}
                />
              )}
              {rightElement && (
                <div className={cn(
                  "absolute right-3",
                  multiline ? "top-3" : "top-1/2 -translate-y-1/2"
                )}>
                  {rightElement}
                </div>
              )}
              {type === 'password' && !disabled && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
            {(error || errorMessage) && (
              <p className="text-sm text-destructive">{(error?.message as string) || errorMessage}</p>
            )}
          </>
        )}
      />
    </div>
  )
}
